import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Database, Search, ChevronLeft, ChevronRight, Trash2, Eye, RefreshCw, Copy } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DatabaseViewer() {
  const { token } = useAuth();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Fetch collections
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/database/collections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.collections) {
        setCollections(data.collections);
      }
    } catch (err) {
      toast.error("Failed to fetch collections");
    }
    setLoading(false);
  };

  // Fetch documents from collection
  const fetchDocuments = async (collectionName, skip = 0) => {
    setLoading(true);
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const res = await fetch(
        `${API}/api/database/collections/${collectionName}?skip=${skip}&limit=${pagination.limit}${searchParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.documents) {
        setDocuments(data.documents);
        setPagination(prev => ({ ...prev, skip, total: data.total }));
      }
    } catch (err) {
      toast.error("Failed to fetch documents");
    }
    setLoading(false);
  };

  // Delete document
  const handleDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    
    try {
      const res = await fetch(`${API}/api/database/collections/${selectedCollection}/${docId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Document deleted");
        fetchDocuments(selectedCollection, pagination.skip);
      } else {
        toast.error("Failed to delete");
      }
    } catch (err) {
      toast.error("Error deleting document");
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(JSON.stringify(text, null, 2));
    toast.success("Copied to clipboard");
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      fetchDocuments(selectedCollection, 0);
    }
  }, [selectedCollection]);

  // Get document ID for display
  const getDocId = (doc) => {
    return doc._id || doc.user_id || doc.project_id || doc.id || "N/A";
  };

  // Get preview text for document
  const getPreview = (doc) => {
    const preview = doc.name || doc.email || doc.title || doc.user_id || doc.project_id || "";
    return preview.length > 40 ? preview.substring(0, 40) + "..." : preview;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="w-8 h-8 text-[#23F7DD]" />
          <div>
            <h1 className="text-2xl font-bold text-white">Database Viewer</h1>
            <p className="text-white/60 text-sm">Browse MongoDB collections and documents</p>
          </div>
        </div>
        <Button onClick={fetchCollections} variant="outline" className="border-white/20 text-white">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Collections Sidebar */}
        <div className="col-span-3">
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg">Collections</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-y-auto">
                {collections.map((coll) => (
                  <button
                    key={coll.name}
                    onClick={() => {
                      setSelectedCollection(coll.name);
                      setSelectedDoc(null);
                      setSearch("");
                    }}
                    className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-white/10 transition-colors ${
                      selectedCollection === coll.name ? "bg-[#23F7DD]/20 border-l-2 border-[#23F7DD]" : ""
                    }`}
                  >
                    <span className="text-white/90 text-sm truncate">{coll.name}</span>
                    <Badge variant="secondary" className="bg-white/10 text-white/70 text-xs">
                      {coll.count}
                    </Badge>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Documents Panel */}
        <div className="col-span-9">
          {selectedCollection ? (
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">
                    {selectedCollection}
                    <span className="text-white/50 font-normal ml-2">({pagination.total} documents)</span>
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                      <Input
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && fetchDocuments(selectedCollection, 0)}
                        className="pl-9 w-64 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                    <Button 
                      onClick={() => fetchDocuments(selectedCollection, 0)} 
                      size="sm"
                      className="bg-[#23F7DD] text-black hover:bg-[#1de0c9]"
                    >
                      Search
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {selectedDoc ? (
                  // Document Detail View
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Button 
                        variant="ghost" 
                        onClick={() => setSelectedDoc(null)}
                        className="text-white/70 hover:text-white"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Back to list
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(selectedDoc)}
                        className="border-white/20 text-white"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy JSON
                      </Button>
                    </div>
                    <pre className="bg-black/50 p-4 rounded-lg overflow-auto max-h-[500px] text-sm text-green-400 font-mono">
                      {JSON.stringify(selectedDoc, null, 2)}
                    </pre>
                  </div>
                ) : (
                  // Documents Table
                  <>
                    <div className="rounded-lg border border-white/10 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="text-white/70">ID</TableHead>
                            <TableHead className="text-white/70">Preview</TableHead>
                            <TableHead className="text-white/70">Fields</TableHead>
                            <TableHead className="text-white/70 text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {documents.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-white/40 py-8">
                                {loading ? "Loading..." : "No documents found"}
                              </TableCell>
                            </TableRow>
                          ) : (
                            documents.map((doc, idx) => (
                              <TableRow key={idx} className="border-white/10">
                                <TableCell className="font-mono text-xs text-white/70">
                                  {getDocId(doc).substring(0, 12)}...
                                </TableCell>
                                <TableCell className="text-white/90">
                                  {getPreview(doc)}
                                </TableCell>
                                <TableCell className="text-white/50 text-sm">
                                  {Object.keys(doc).length} fields
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setSelectedDoc(doc)}
                                      className="text-white/70 hover:text-[#23F7DD]"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => copyToClipboard(doc)}
                                      className="text-white/70 hover:text-blue-400"
                                    >
                                      <Copy className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDelete(getDocId(doc))}
                                      className="text-white/70 hover:text-red-400"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-white/50 text-sm">
                        Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.skip === 0}
                          onClick={() => fetchDocuments(selectedCollection, Math.max(0, pagination.skip - pagination.limit))}
                          className="border-white/20 text-white disabled:opacity-30"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.skip + pagination.limit >= pagination.total}
                          onClick={() => fetchDocuments(selectedCollection, pagination.skip + pagination.limit)}
                          className="border-white/20 text-white disabled:opacity-30"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="flex flex-col items-center justify-center py-20">
                <Database className="w-16 h-16 text-white/20 mb-4" />
                <p className="text-white/50">Select a collection to view documents</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
